import React, { useRef } from 'react'
import { connect } from 'react-redux'
import { Field, reduxForm, InjectedFormProps } from 'redux-form'
import { compose } from '@reduxjs/toolkit'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

import { RootState } from 'typedRedux/configureStoreWithHistory'

import useDispatch from 'hooks/useDispatch'
import { registerUser } from 'actions/authActions'
import { renderTextField, Submit } from 'components/form'
import {
  validRequired,
  validEmail,
  validPassword,
} from 'components/form/validate'
import { dispatchRollbarError } from 'utils/rollbar'

import UserLocationField from './UserLocationField'
import withCurrentVisit from './withCurrentVisit'
import {
  AuthToken,
  Terms,
  FacebookLogin,
  GoogleLogin,
  AppleLogin,
} from './shared'
import { Divider, FieldWrapper } from './styles'

interface SignupFormProps extends InjectedFormProps {
  auth: { loading: boolean }
}

interface SignupProps {
  onSuccess?: () => any
  onConfirmEmail: () => any
  serverSide?: boolean
  referredById?: string
  referralType?: string
}

interface VisitProps {
  location: {
    name: string
    lng: number
    lat: number
  }
}

const Signup = ({
  handleSubmit,
  auth,
  onSuccess,
  onConfirmEmail,
  form,
  serverSide = false,
  referredById,
  referralType,
}: SignupFormProps & SignupProps) => {
  const dispatch = useDispatch()
  const formElement = useRef<HTMLFormElement>()

  const { executeRecaptcha } = useGoogleReCaptcha()

  const setRecaptchaToken = (token: string) => {
    const input = formElement.current.elements.namedItem(
      'recaptcha_token'
    ) as HTMLInputElement
    if (input) {
      input.value = token
    }
  }

  const onRecaptchaError = (error?: Error) => {
    dispatchRollbarError('Recaptcha failed at signup', {
      originalError: error?.toString(),
    })
  }

  const fetchRecaptchaToken = async () => {
    if (!executeRecaptcha) {
      return null
    }

    try {
      const token = await executeRecaptcha('signup')
      return token
    } catch (error) {
      onRecaptchaError(error)
      return null
    }
  }

  const onSubmit = async (formValues: any) => {
    dispatch({ type: 'USER_REGISTER_PENDING' })
    const recaptchaToken = await fetchRecaptchaToken()

    if (serverSide) {
      setRecaptchaToken(recaptchaToken)
      formElement.current.submit()
    } else {
      dispatch(
        registerUser({
          ...formValues,
          recaptcha_token: recaptchaToken,
        })
        // @ts-ignore -- registerUser not typed
      ).then(onSuccess)
    }
  }

  return (
    <>
      <AppleLogin>Sign up with Apple</AppleLogin>
      <GoogleLogin>Sign up with Google</GoogleLogin>
      <FacebookLogin
        disabled={auth.loading}
        onSuccess={onSuccess}
        onConfirmEmail={onConfirmEmail}
        serverSide={serverSide}
      >
        Sign up with Facebook
      </FacebookLogin>

      <form
        className="qa-sign-up"
        action="/users"
        method="post"
        ref={formElement}
        onSubmit={handleSubmit(onSubmit)}
      >
        <AuthToken />

        <Divider>
          <span>or</span>
        </Divider>

        <FieldWrapper>
          <Field
            label="Email"
            name="user[email]"
            type="email"
            component={renderTextField}
            validate={[validRequired, validEmail]}
            noMargin
          />
        </FieldWrapper>

        <FieldWrapper>
          <Field
            label="Password"
            name="user[password]"
            type="password"
            component={renderTextField}
            validate={[validRequired, validPassword]}
            noMargin
          />
        </FieldWrapper>

        <FieldWrapper>
          <Field
            label="Confirm password"
            name="user[password_confirmation]"
            type="password"
            component={renderTextField}
            validate={[validRequired, validPassword]}
            noMargin
          />
        </FieldWrapper>

        <FieldWrapper columns>
          <Field
            label="First name"
            name="user[first_name]"
            component={renderTextField}
            validate={[validRequired]}
            noMargin
          />
          <Field
            label="Last name"
            name="user[last_name]"
            component={renderTextField}
            validate={[validRequired]}
            noMargin
          />
        </FieldWrapper>

        <FieldWrapper margin="medium">
          {/* @ts-ignore -- not typed yet */}
          <UserLocationField formName={form} noMargin />
        </FieldWrapper>

        {referredById && (
          <Field
            name="user[referred_by_id]"
            type="hidden"
            component="input"
            value={referredById}
          />
        )}

        {referralType && (
          <Field
            name="user[referral_type]"
            type="hidden"
            component="input"
            value="travel_crew"
          />
        )}

        <Field name="recaptcha_token" type="hidden" component="input" />

        <FieldWrapper>
          <Submit
            disabled={auth.loading}
            fullwidth
            uppercase={false}
            aria-label="Submit sign up form"
          >
            Sign up
          </Submit>
        </FieldWrapper>

        <Terms />
      </form>
    </>
  )
}

const SignupForm = reduxForm({
  form: 'Signup',
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
})(Signup)

const ConnectedSignupForm: React.FC<SignupProps> = compose(
  withCurrentVisit,
  connect(
    (
      { auth }: { auth: RootState['auth'] },
      { currentVisit }: { currentVisit: VisitProps }
    ) => ({
      auth,
      initialValues: {
        user: {
          location: currentVisit?.location?.name,
          longitude: currentVisit?.location?.lng,
          latitude: currentVisit?.location?.lat,
          referred_by_id: auth?.referredById,
          referral_type: auth?.referralType,
        },
      },
    })
  )
)(SignupForm)

export default ConnectedSignupForm
